import React, { useState } from "react";
import {
  createTopicCategory,
  updateTopicCategory,
  deleteTopicCategory,
  reorderTopicCategories,
} from "../api";
import Surface from "../theme/Surface";
import { CategoryIcon, CATEGORY_ICONS, CATEGORY_ICON_KEYS } from "../categoryIcons";

/**
 * Admin panel for sidebar topic categories: add, rename, reorder (up/down —
 * works on touch where drag doesn't), and delete. Deleting a category never
 * deletes topics; they fall back to the top level of the list.
 *
 * `categories` is the ordered list from the parent; every mutation calls
 * `onChanged(nextCategories)` so the parent (App) stays the source of truth.
 */
export default function TopicCategoryManager({ categories, topicCounts = {}, onChanged, onClose }) {
  const [newName, setNewName] = useState("");
  const [newIncludeInReview, setNewIncludeInReview] = useState(true);
  const [newIcon, setNewIcon] = useState("");
  const [newRestricted, setNewRestricted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editIncludeInReview, setEditIncludeInReview] = useState(true);
  const [editIcon, setEditIcon] = useState("");
  const [editRestricted, setEditRestricted] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  async function run(fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  function handleCreate(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    run(async () => {
      const created = await createTopicCategory({ name, include_in_review: newIncludeInReview, icon: newIcon, is_restricted: newRestricted });
      setNewName("");
      setNewIncludeInReview(true);
      setNewIcon("");
      setNewRestricted(false);
      onChanged([...categories, created]);
    });
  }

  function startEdit(cat) {
    setEditingId(cat.id);
    setEditName(cat.name);
    setEditDescription(cat.description || "");
    setEditIncludeInReview(cat.include_in_review !== false);
    setEditIcon(cat.icon || "");
    setEditRestricted(!!cat.is_restricted);
    setConfirmDeleteId(null);
    setError(null);
  }

  function handleSaveEdit(e) {
    e.preventDefault();
    const name = editName.trim();
    if (!name) return;
    run(async () => {
      const updated = await updateTopicCategory(editingId, {
        name,
        description: editDescription.trim(),
        include_in_review: editIncludeInReview,
        icon: editIcon,
        is_restricted: editRestricted,
      });
      onChanged(categories.map(c => (c.id === updated.id ? updated : c)));
      setEditingId(null);
    });
  }

  function handleDelete(id) {
    run(async () => {
      await deleteTopicCategory(id);
      onChanged(categories.filter(c => c.id !== id));
      setConfirmDeleteId(null);
    });
  }

  function handleMove(idx, delta) {
    const target = idx + delta;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    const renumbered = next.map((c, i) => ({ ...c, order: i + 1 }));
    run(async () => {
      await reorderTopicCategories(renumbered.map(c => c.id));
      onChanged(renumbered);
    });
  }

  const iconBtn = "p-1 rounded shrink-0 transition-colors hover:brightness-125 disabled:opacity-30";

  return (
    <Surface glass className="p-3 mb-3" data-testid="category-manager">
      <div className="flex items-center justify-between mb-2">
        <span className="font-cormorant text-lg font-semibold" style={{ color: "var(--gold-bright)" }}>Categories</span>
        <button
          type="button"
          onClick={onClose}
          className="text-xs px-2 py-0.5 rounded border transition-colors hover:brightness-110"
          style={{ color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
        >
          Done
        </button>
      </div>

      <form onSubmit={handleCreate} className="mb-2 space-y-1.5">
        <div className="flex gap-1.5">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="New category name"
            maxLength={120}
            className="dt-field flex-1 min-w-0 px-2 py-1.5 text-sm"
            aria-label="New category name"
          />
          <button
            type="submit"
            disabled={busy || !newName.trim()}
            className="px-2.5 py-1 rounded-lg text-xs font-semibold disabled:opacity-50 shrink-0"
            style={{ color: "var(--ink)", background: "var(--gold)" }}
          >
            Add
          </button>
        </div>
        <IconPicker value={newIcon} onChange={setNewIcon} idPrefix="new" />
        <ReviewCheckbox checked={newIncludeInReview} onChange={setNewIncludeInReview} label="Include in Start Review" />
        <ReviewCheckbox
          checked={newRestricted}
          onChange={setNewRestricted}
          label="Restricted (group members only)"
          title="Hidden from everyone except admins and members of a category group that includes it (Admin panel → Category groups)"
        />
      </form>

      {error && <p className="text-xs mb-2" style={{ color: "var(--tag-rose)" }}>{error}</p>}

      {categories.length === 0 ? (
        <p className="text-xs py-1" style={{ color: "var(--parchment-dim)" }}>
          No categories yet. Add one above, then assign topics to it from Edit Order or the topic page.
        </p>
      ) : (
        <ul className="list-none p-0 space-y-1.5">
          {categories.map((cat, idx) => {
            const count = topicCounts[cat.id] || 0;
            if (editingId === cat.id) {
              return (
                <li key={cat.id}>
                  <form onSubmit={handleSaveEdit} className="rounded-lg p-2 space-y-1.5" style={{ border: "1px solid var(--panel-line)" }}>
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      maxLength={120}
                      className="dt-field w-full px-2 py-1 text-sm"
                      aria-label="Category name"
                      autoFocus
                    />
                    <input
                      type="text"
                      value={editDescription}
                      onChange={e => setEditDescription(e.target.value)}
                      placeholder="Short description (optional)"
                      maxLength={255}
                      className="dt-field w-full px-2 py-1 text-xs"
                      aria-label="Category description"
                    />
                    <IconPicker value={editIcon} onChange={setEditIcon} idPrefix={`edit-${cat.id}`} />
                    <ReviewCheckbox checked={editIncludeInReview} onChange={setEditIncludeInReview} label="Include in Start Review" />
                    <ReviewCheckbox
                      checked={editRestricted}
                      onChange={setEditRestricted}
                      label="Restricted (group members only)"
                      title="Hidden from everyone except admins and members of a category group that includes it (Admin panel → Category groups)"
                    />
                    <div className="flex gap-1.5">
                      <button
                        type="submit"
                        disabled={busy || !editName.trim()}
                        className="px-2 py-0.5 rounded text-xs font-semibold disabled:opacity-50"
                        style={{ color: "var(--ink)", background: "var(--gold)" }}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="px-2 py-0.5 rounded text-xs border"
                        style={{ color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </li>
              );
            }
            if (confirmDeleteId === cat.id) {
              return (
                <li key={cat.id} className="rounded-lg p-2 text-xs" style={{ border: "1px solid var(--tag-rose)" }}>
                  <div className="mb-1.5" style={{ color: "var(--parchment)" }}>
                    Delete <span className="font-semibold">{cat.name}</span>?
                    {count > 0 && <> Its {count} topic{count === 1 ? "" : "s"} will move back to the top level.</>}
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleDelete(cat.id)}
                      disabled={busy}
                      className="px-2 py-0.5 rounded font-semibold disabled:opacity-50"
                      style={{ color: "#fff", background: "var(--tag-rose)" }}
                    >
                      Yes, delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(null)}
                      className="px-2 py-0.5 rounded border"
                      style={{ color: "var(--parchment-dim)", borderColor: "var(--panel-line)" }}
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              );
            }
            return (
              <li key={cat.id} className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm" style={{ border: "1px solid var(--panel-line)", color: "var(--parchment)" }}>
                <CategoryIcon icon={cat.icon} className="w-3.5 h-3.5 shrink-0 mr-0.5" />
                <span className="truncate flex-1">{cat.name}</span>
                {cat.is_restricted && (
                  <span
                    className="text-[10px] px-1 py-0.5 rounded border shrink-0 flex items-center gap-0.5"
                    style={{ color: "var(--gold-bright)", borderColor: "var(--panel-line)" }}
                    title="Restricted: only admins and category-group members can see it"
                    data-testid="category-restricted-badge"
                  >
                    <LockIcon className="w-2.5 h-2.5" />
                    Restricted
                  </span>
                )}
                {cat.include_in_review === false && (
                  <span
                    className="text-[10px] px-1 py-0.5 rounded border shrink-0"
                    style={{ color: "var(--slate)", borderColor: "var(--panel-line)" }}
                    title="Start Review skips this category's topics"
                  >
                    No review
                  </span>
                )}
                <span className="text-[11px] tabular-nums shrink-0" style={{ color: "var(--parchment-dim)" }}>{count}</span>
                <button type="button" onClick={() => handleMove(idx, -1)} disabled={busy || idx === 0} className={iconBtn} style={{ color: "var(--parchment-dim)" }} title="Move up" aria-label={`Move ${cat.name} up`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                </button>
                <button type="button" onClick={() => handleMove(idx, 1)} disabled={busy || idx === categories.length - 1} className={iconBtn} style={{ color: "var(--parchment-dim)" }} title="Move down" aria-label={`Move ${cat.name} down`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                <button type="button" onClick={() => startEdit(cat)} disabled={busy} className={iconBtn} style={{ color: "var(--parchment-dim)" }} title="Rename" aria-label={`Rename ${cat.name}`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                </button>
                <button type="button" onClick={() => { setConfirmDeleteId(cat.id); setEditingId(null); }} disabled={busy} className={iconBtn} style={{ color: "var(--parchment-dim)" }} title="Delete" aria-label={`Delete ${cat.name}`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Surface>
  );
}

/** "Include in Start Review" tick box shared by the create and edit forms.
 *  Off = Start Review never picks this category's topics. */
function ReviewCheckbox({ checked, onChange, label, title }) {
  return (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" style={{ color: "var(--parchment-dim)" }} title={title}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-3.5 h-3.5 rounded"
        style={{ accentColor: "var(--gold)" }}
      />
      {label}
    </label>
  );
}

export function LockIcon({ className = "w-3.5 h-3.5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

/** Grid of the built-in category icons (folder first). Radio-style buttons so
 *  the choice is keyboard/touch friendly; the selected one gets the gold ring. */
function IconPicker({ value, onChange, idPrefix }) {
  return (
    <div role="radiogroup" aria-label="Category icon" className="flex flex-wrap gap-1" data-testid={`${idPrefix}-icon-picker`}>
      {CATEGORY_ICON_KEYS.map((key) => {
        const selected = (value || "") === key;
        return (
          <button
            key={key || "folder"}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={CATEGORY_ICONS[key].label}
            title={CATEGORY_ICONS[key].label}
            onClick={() => onChange(key)}
            className="w-7 h-7 rounded-md flex items-center justify-center border transition-colors hover:brightness-125"
            style={selected
              ? { borderColor: "var(--gold)", background: "var(--gold-bg)", boxShadow: "0 0 0 1px var(--gold)" }
              : { borderColor: "var(--panel-line)", background: "transparent" }}
          >
            <CategoryIcon icon={key} className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}
